/**
 * CreateTenantDialog — one-call create through tmm-api (TMMPDM-0021 / TMMAPI-0076).
 * Fields: slug, name (≤ 200), reseller (required), signup mode (default Invite only),
 * currency (default USD). Timeout / 502 SAAS_AUTH_OUTCOME_UNKNOWN / 500 TENANT_SETUP_INCOMPLETE
 * keep the dialog open with Try again (identical body, never automatic).
 * TMMPDM-0029: a 409 reseller refusal shows the shared `resellerRefusalMessage`.
 * TMMPDM-0032: 502 `UPSTREAM_UNAVAILABLE` (tmm-api 0096 — nothing was written) says so, with the
 * same Try again (identical body).
 */
import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Link,
  Stack,
  TextField,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useCopy } from '@/hooks/useCopy';
import { SubmitButton } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import {
  ApiError,
  errorCopy,
  isUpstreamUnavailable,
  isValidationFailed,
  resellerRefusalMessage,
  upstreamUnavailableMessage,
  validationFields,
} from '@/api/errors';
import {
  SIGNUP_MODES,
  type ResellerDto,
  type SignupMode,
  type TenantCreateRequest,
} from '@/api/tmm/types';
import { provisionCreateWarning } from './provisionBilling';

const CURRENCIES = ['USD', 'CRC', 'MXN', 'EUR', 'GBP', 'CAD'] as const;

/** DNS label: lowercase letters/digits, hyphens not at either end, ≤63 chars. */
const SLUG_PATTERN = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

interface CreateTenantDialogProps {
  open: boolean;
  /** The product's resellers (from `useResellerNames`); only explicitly tenant-less ones are offered. */
  resellers: ResellerDto[];
  /** When opened from a reseller's Tenants tab, that reseller is preselected. */
  defaultResellerId?: number | undefined;
  /** Exact create/read result; no dependency on a preloaded reseller list. */
  presetReseller?: ResellerDto | undefined;
  onPendingChange?: (pending: boolean) => void;
  onClose: () => void;
  /** `billingWarning` is set when the tenant exists but its billing provisioning FAILED (TMMPDM-0084). */
  onCreated: (slug: string, billingWarning?: string) => void;
  /** New partner step 2 closes on any server failure; never retries automatically. */
  onFailed?: (error: unknown) => void;
}

interface FormState {
  slug: string;
  name: string;
  signupMode: SignupMode;
  resellerId: string;
  currency: string;
}

function initialForm(defaultResellerId?: number): FormState {
  return {
    slug: '',
    name: '',
    signupMode: 'INVITE_ONLY',
    resellerId: defaultResellerId !== undefined ? String(defaultResellerId) : '',
    currency: 'USD',
  };
}

/** The field-level refusals the dialog renders inline (one list; everything else is a form error). */
const FIELD_ERROR_CODES: Record<'slugTaken' | 'resellerRequired', string> = {
  slugTaken: 'TENANT_SLUG_TAKEN',
  resellerRequired: 'RESELLER_REQUIRED',
};

function isRetryableCreateFailure(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  if (err.code === 'NETWORK_ERROR') return true;
  const code = String(err.code);
  return code === 'SAAS_AUTH_OUTCOME_UNKNOWN' || code === 'TENANT_SETUP_INCOMPLETE';
}

export function CreateTenantDialog({
  open,
  resellers,
  defaultResellerId: requestedResellerId,
  presetReseller,
  onClose,
  onCreated,
}: CreateTenantDialogProps) {
  const defaultResellerId = presetReseller?.id ?? requestedResellerId;
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      {open ? (
        <CreateTenantForm
          key={defaultResellerId ?? 'roster'}
          resellers={resellers}
          presetReseller={presetReseller}
          defaultResellerId={defaultResellerId}
          onClose={onClose}
          onCreated={onCreated}
        />
      ) : null}
    </Dialog>
  );
}

export function CreateTenantForm({
  resellers,
  defaultResellerId: requestedResellerId,
  presetReseller,
  onClose,
  onCreated,
  onFailed,
  onPendingChange,
}: Omit<CreateTenantDialogProps, 'open'>) {
  const defaultResellerId = presetReseller?.id ?? requestedResellerId;
  const availableResellers = presetReseller ? [presetReseller] : resellers;
  const { t } = useCopy();
  const [form, setForm] = useState<FormState>(() => initialForm(defaultResellerId));
  const [pendingBody, setPendingBody] = useState<TenantCreateRequest | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [resellerError, setResellerError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [retryHint, setRetryHint] = useState(false);
  const [existingSlug, setExistingSlug] = useState<string | null>(null);

  const fieldsLocked = pendingBody !== null && retryHint;

  const resetAndClose = () => {
    setForm(initialForm(defaultResellerId));
    setPendingBody(null);
    setSlugError(null);
    setNameError(null);
    setResellerError(null);
    setFieldErrors({});
    setFormError(null);
    setRetryHint(false);
    onClose();
  };

  const validate = (): boolean => {
    let ok = true;
    if (form.slug.trim() === '') {
      setSlugError(t('tenants.create.slugRequired', 'Slug is required.'));
      ok = false;
    } else if (!SLUG_PATTERN.test(form.slug.trim())) {
      setSlugError(
        t(
          'tenants.create.slugInvalid',
          'Slug must be a valid DNS label (lowercase letters, numbers, hyphens; up to 63 characters).',
        ),
      );
      ok = false;
    } else {
      setSlugError(null);
    }
    if (form.name.trim() === '') {
      setNameError(t('tenants.create.nameRequired', 'Name is required.'));
      ok = false;
    } else if (form.name.trim().length > 200) {
      setNameError(t('tenants.create.nameTooLong', 'Name must be 200 characters or fewer.'));
      ok = false;
    } else {
      setNameError(null);
    }
    if (
      !availableResellers.some(
        (reseller) => String(reseller.id) === form.resellerId && reseller.tenant === null,
      )
    ) {
      setResellerError(t('tenants.create.resellerRequired', 'Reseller is required.'));
      ok = false;
    } else {
      setResellerError(null);
    }
    return ok;
  };

  const send = async (body: TenantCreateRequest) => {
    setSubmitting(true);
    onPendingChange?.(true);
    setFormError(null);
    setExistingSlug(null);
    setSlugError(null);
    setNameError(null);
    setResellerError(null);
    setFieldErrors({});
    try {
      const tenant = await adminApi.createTenant(body);
      setPendingBody(null);
      setRetryHint(false);
      setForm(initialForm(defaultResellerId));
      // The create never fails on provisioning: a FAILED outcome is a tenant that still exists.
      if (tenant.billingProvisioning?.outcome === 'FAILED') {
        onCreated(tenant.slug, provisionCreateWarning(tenant.billingProvisioning.reason, t));
      } else {
        onCreated(tenant.slug);
      }
    } catch (err) {
      const fieldFailure =
        isValidationFailed(err) ||
        (err instanceof ApiError && Object.values(FIELD_ERROR_CODES).includes(String(err.code)));
      if (onFailed && !fieldFailure) {
        onFailed(err);
        return;
      }
      if (err instanceof ApiError && String(err.code) === 'RESELLER_HAS_TENANT') {
        setRetryHint(false);
        setPendingBody(null);
        const slug = err.details.backend?.['tenantSlug'];
        setExistingSlug(typeof slug === 'string' && slug.length > 0 ? slug : null);
        setFormError(t('tenants.create.hasTenant', 'This reseller already has a tenant.'));
        return;
      }
      if (isUpstreamUnavailable(err)) {
        setPendingBody(body);
        setRetryHint(true);
        setFormError(upstreamUnavailableMessage(t));
        return;
      }
      if (isRetryableCreateFailure(err)) {
        setPendingBody(body);
        setRetryHint(true);
        setFormError(
          t(
            'tenants.create.retrySafe',
            "We couldn't confirm the tenant was fully created. Trying again is safe.",
          ),
        );
        return;
      }
      const refusal = resellerRefusalMessage(err, t);
      if (
        err instanceof ApiError &&
        err.status === 404 &&
        String(err.code) === 'RESELLER_NOT_FOUND'
      ) {
        setFormError(t('partners.notFound', 'Partner not found'));
        return;
      }
      if (isValidationFailed(err)) {
        const fields = validationFields(err, t, {
          slug: t(
            'tenants.create.slugInvalid',
            'Slug must be a valid DNS label (lowercase letters, numbers, hyphens; up to 63 characters).',
          ),
        });
        setFieldErrors(fields);
        if (fields.slug) setSlugError(fields.slug);
        if (fields.name) setNameError(fields.name);
        if (fields.resellerId) setResellerError(fields.resellerId);
        // A field the form doesn't show is said once, in the banner; shown fields are said at the field.
        if (
          Object.keys(fields).some(
            (field) => !['slug', 'name', 'resellerId', 'currency'].includes(field),
          )
        ) {
          setFormError(
            errorCopy(
              err,
              t,
              t('tenants.create.errorGeneric', 'Could not create tenant. Please try again.'),
            ),
          );
        }
      } else if (err instanceof ApiError && String(err.code) === FIELD_ERROR_CODES.slugTaken) {
        setSlugError(
          t(
            'tenants.create.errorDuplicateSlug',
            'That slug is already registered. Choose a different one.',
          ),
        );
      } else if (
        err instanceof ApiError &&
        String(err.code) === FIELD_ERROR_CODES.resellerRequired
      ) {
        setResellerError(t('tenants.create.resellerRequired', 'Reseller is required.'));
      } else if (refusal !== null) {
        setFormError(refusal);
      } else {
        setFormError(
          errorCopy(
            err,
            t,
            t('tenants.create.errorGeneric', 'Could not create tenant. Please try again.'),
          ),
        );
      }
    } finally {
      setSubmitting(false);
      onPendingChange?.(false);
    }
  };

  const handleSubmit = async () => {
    if (pendingBody !== null && retryHint) {
      await send(pendingBody);
      return;
    }
    if (!validate()) return;
    const body: TenantCreateRequest = {
      slug: form.slug.trim(),
      name: form.name.trim(),
      resellerId: Number(form.resellerId),
      signupMode: form.signupMode,
      currency: form.currency,
    };
    setPendingBody(body);
    await send(body);
  };

  const tenantlessResellers = availableResellers.filter((reseller) => reseller.tenant === null);

  return (
    <>
      <DialogTitle>{t('tenants.create.title', 'Create tenant')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {formError ? (
            <Alert
              severity={retryHint ? 'warning' : 'error'}
              action={
                retryHint ? (
                  <SubmitButton
                    variant="text"
                    color="inherit"
                    busy={submitting}
                    onClick={() => void handleSubmit()}
                  >
                    {t('tenants.create.tryAgain', 'Try again')}
                  </SubmitButton>
                ) : undefined
              }
            >
              {existingSlug ? (
                <Link
                  component={RouterLink}
                  to={`/partners/by-tenant/${encodeURIComponent(existingSlug)}`}
                >
                  {formError}
                </Link>
              ) : (
                formError
              )}
            </Alert>
          ) : null}
          <TextField
            label={t('tenants.create.slugLabel', 'Slug')}
            helperText={
              slugError ??
              t(
                'tenants.create.slugHelper',
                'Lowercase letters, numbers, hyphens — up to 63 characters.',
              )
            }
            error={Boolean(slugError)}
            value={form.slug}
            onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
            size="small"
            fullWidth
            disabled={fieldsLocked}
          />
          <TextField
            label={t('tenants.create.nameLabel', 'Name')}
            helperText={nameError ?? undefined}
            error={Boolean(nameError)}
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            size="small"
            fullWidth
            disabled={fieldsLocked}
          />
          <TextField
            select
            label={t('tenants.create.resellerLabel', 'Reseller')}
            value={form.resellerId}
            onChange={(e) => setForm((prev) => ({ ...prev, resellerId: e.target.value }))}
            helperText={
              resellerError ?? t('tenants.create.onePerReseller', 'Each reseller has one tenant.')
            }
            error={Boolean(resellerError)}
            size="small"
            fullWidth
            required
            disabled={fieldsLocked || defaultResellerId !== undefined}
            slotProps={{
              formHelperText: { sx: { '&.Mui-disabled': { color: 'text.secondary' } } },
            }}
          >
            {tenantlessResellers.map((reseller) => (
              <MenuItem key={reseller.id} value={String(reseller.id)}>
                {reseller.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('tenants.create.signupModeLabel', 'Signup mode')}
            value={form.signupMode}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, signupMode: e.target.value as SignupMode }))
            }
            size="small"
            fullWidth
            disabled={fieldsLocked}
          >
            {SIGNUP_MODES.map((mode) => (
              <MenuItem key={mode} value={mode}>
                {t(`tenants.signupMode.${mode}`, mode)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('tenants.create.currencyLabel', 'Currency')}
            value={form.currency}
            onChange={(e) => setForm((prev) => ({ ...prev, currency: e.target.value }))}
            helperText={fieldErrors.currency}
            error={Boolean(fieldErrors.currency)}
            size="small"
            fullWidth
            disabled={fieldsLocked}
          >
            {CURRENCIES.map((code) => (
              <MenuItem key={code} value={code}>
                {code}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={resetAndClose} disabled={submitting}>
          {t('tenants.create.cancelButton', 'Cancel')}
        </Button>
        {!retryHint ? (
          <SubmitButton onClick={() => void handleSubmit()} busy={submitting}>
            {submitting
              ? t('tenants.create.submitting', 'Creating…')
              : t('tenants.create.submitButton', 'Create')}
          </SubmitButton>
        ) : null}
      </DialogActions>
    </>
  );
}
